import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  Lock as LockIcon,
  Share as ShareIcon,
  Person as PersonIcon,
  ChevronRight as ChevronRightIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  TableRestaurant as TableRestaurantIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ArrowBack as ArrowBackIcon,
  DriveFileMoveOutlined as MoveToFolderIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { useInventoryContext, InventoryFolder } from '../contexts/InventoryContext';
import { isLoaned, isOverdue, describeLoan } from '../utils/loanStatus';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate } from 'react-router-dom';
import { EmptyInventoryState } from '../components/inventory/EmptyInventoryState';
import { useLimits } from '../hooks/useLimits';
import { LimitReachedDialog } from '../components/subscription/LimitReachedDialog';
import { useSubscription } from '../contexts/SubscriptionContext';
import { ShareInventoryAccessDialog } from '../components/inventory/ShareInventoryAccessDialog';
import {
  getInventoriesIHaveAccessTo,
  InventoryAccess,
} from '../services/inventorySharingService';

export const Inventory: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    items,
    loading,
    searchItems,
    deleteItem,
    updateItem,
    folders,
    createFolder,
    renameFolder,
    deleteFolder,
  } = useInventoryContext();
  const { canAddInventoryItem } = useLimits();
  const { plan, isPro, canAccessFeature } = useSubscription();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [limitDialog, setLimitDialog] = useState({ open: false, message: '' });
  const [currentTab, setCurrentTab] = useState(0);
  const [shareAccessDialogOpen, setShareAccessDialogOpen] = useState(false);
  const [sharedInventories, setSharedInventories] = useState<InventoryAccess[]>([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderDialogMode, setFolderDialogMode] = useState<'create' | 'rename'>('create');
  const [folderNameInput, setFolderNameInput] = useState('');
  const [folderBeingRenamed, setFolderBeingRenamed] = useState<InventoryFolder | null>(null);
  const [savingFolder, setSavingFolder] = useState(false);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<InventoryFolder | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
  const [itemToMove, setItemToMove] = useState<string | null>(null);

  useEffect(() => {
    if (currentFolderId && !folders.some((folder) => folder.id === currentFolderId)) {
      setCurrentFolderId(null);
    }
  }, [folders, currentFolderId]);

  // Carregar inventários compartilhados quando mudar para a tab
  useEffect(() => {
    if (currentTab === 2 && user?.uid) {
      loadSharedInventories();
    }
  }, [currentTab, user?.uid]);

  const loadSharedInventories = async () => {
    if (!user?.uid) return;
    setLoadingShared(true);
    try {
      const accesses = await getInventoriesIHaveAccessTo(user.uid);
      setSharedInventories(accesses);
    } catch (error) {
      console.error('Erro ao carregar inventários compartilhados:', error);
    } finally {
      setLoadingShared(false);
    }
  };

  // Obter categorias únicas dos itens
  const categories = ['all', ...Array.from(new Set(items.map(item => item.category).filter(Boolean)))];

  const folderIds = new Set(folders.map((folder) => folder.id));
  const countItemsInFolder = (folderId: string) =>
    items.filter((item) => item.folderId === folderId).length;
  const currentFolder = currentFolderId
    ? folders.find((folder) => folder.id === currentFolderId) || null
    : null;

  // Filtrar itens por busca e categoria
  let displayItems = searchTerm.trim() ? searchItems(searchTerm) : items;
  if (selectedCategory !== 'all') {
    displayItems = displayItems.filter(item => item.category === selectedCategory);
  }
  displayItems = displayItems.filter((item) => {
    if (currentFolderId === null) {
      return !item.folderId || !folderIds.has(item.folderId);
    }
    return item.folderId === currentFolderId;
  });
  const loanedCount = items.filter(item => isLoaned(item)).length;

  const handleDeleteClick = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    setItemToDelete(itemId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      await deleteItem(itemToDelete);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      alert('Erro ao excluir item');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    navigate(`/inventory/edit/${itemId}`);
  };

  const openCreateFolder = () => {
    setFolderDialogMode('create');
    setFolderBeingRenamed(null);
    setFolderNameInput('');
    setFolderDialogOpen(true);
  };

  const openRenameFolder = (folder: InventoryFolder) => {
    setFolderDialogMode('rename');
    setFolderBeingRenamed(folder);
    setFolderNameInput(folder.name);
    setFolderDialogOpen(true);
  };

  const handleSaveFolder = async () => {
    const name = folderNameInput.trim();
    if (!name) return;

    setSavingFolder(true);
    try {
      if (folderDialogMode === 'create') {
        await createFolder(name);
      } else if (folderBeingRenamed) {
        await renameFolder(folderBeingRenamed.id, name);
      }
      setFolderDialogOpen(false);
      setFolderBeingRenamed(null);
      setFolderNameInput('');
    } catch (error) {
      alert('Erro ao salvar a pasta');
    } finally {
      setSavingFolder(false);
    }
  };

  const handleDeleteFolderClick = (folder: InventoryFolder) => {
    setFolderToDelete(folder);
    setDeleteFolderDialogOpen(true);
  };

  const handleDeleteFolderConfirm = async () => {
    if (!folderToDelete) return;

    setDeletingFolder(true);
    try {
      const deletedId = folderToDelete.id;
      await deleteFolder(deletedId);
      if (currentFolderId === deletedId) {
        setCurrentFolderId(null);
      }
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
    } catch (error) {
      alert('Erro ao excluir a pasta');
    } finally {
      setDeletingFolder(false);
    }
  };

  const openMoveMenu = (e: React.MouseEvent<HTMLElement>, itemId: string) => {
    e.stopPropagation();
    setItemToMove(itemId);
    setMoveMenuAnchor(e.currentTarget);
  };

  const closeMoveMenu = () => {
    setMoveMenuAnchor(null);
    setItemToMove(null);
  };

  const handleMoveToFolder = async (folderId: string | null) => {
    if (!itemToMove) return;
    const targetItemId = itemToMove;
    closeMoveMenu();
    try {
      await updateItem(targetItemId, { folderId: folderId || undefined });
    } catch (error) {
      alert('Erro ao mover o item');
    }
  };

  const getMovingItemFolderId = (): string | undefined => {
    if (!itemToMove) return undefined;
    return items.find((item) => item.id === itemToMove)?.folderId;
  };

  if (loading) {
    return (
      <>
        <TopBar
          title="Acervo"
          icon={<HomeIcon sx={{ fontSize: '0.875rem' }} />}
          showBackButton
          onBackClick={() => navigate('/')}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <TopBar
        title="Acervo"
        icon={<HomeIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        showMoreButton={true}
        onBackClick={() => navigate('/dashboard')}
        onSearchClick={() => {
          // Tentar focar no input de busca se ele existir
          const searchInput = document.querySelector('input[placeholder="Buscar itens..."]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        onMoreClick={() => {
          setShowCreateDialog(true);
        }}
      />
      <Box sx={{
        paddingTop: '70px',
        paddingBottom: '120px',
        maxWidth: '1200px',
        margin: '0 auto',
        px: 3,
        minHeight: '100vh',
      }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            if (!canAddInventoryItem) {
              setLimitDialog({
                open: true,
                message: `Limite de itens atingido no plano ${plan}.`
              });
              return;
            }
            setShowCreateDialog(true);
          }}
          sx={{
            backgroundColor: theme.colors.secondary,
            color: theme.colors.text.onSecondary,
            borderRadius: theme.radii.medium,
            py: 1.5,
            mb: 3,
            textTransform: 'uppercase',
            fontWeight: 'bold',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.colors.secondary,
              filter: 'brightness(0.9)',
              opacity: 0.9,
              boxShadow: 'none',
            }
          }}
          startIcon={<AddIcon />}
        >
          ADICIONAR ITEM
        </Button>

        {isPro && (
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/mesa')}
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              borderRadius: theme.radii.medium,
              py: 1.5,
              mb: 2,
              textTransform: 'uppercase',
              fontWeight: 'bold',
              boxShadow: 'none',
              '&:hover': { backgroundColor: theme.colors.primary, filter: 'brightness(0.95)', boxShadow: 'none' },
            }}
            startIcon={<TableRestaurantIcon />}
          >
            Acesse sua mesa virtual
          </Button>
        )}

        {/* Botão Compartilhar Meu Inventário - sempre visível para Basic/Pro */}
        {(isPro || canAccessFeature('inventory-sharing')) && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setShareAccessDialogOpen(true)}
            sx={{
              borderColor: theme.colors.primary,
              color: theme.colors.primary,
              borderRadius: theme.radii.medium,
              py: 1.5,
              mb: 3,
              textTransform: 'uppercase',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: theme.colors.primary,
                backgroundColor: `${theme.colors.primary}10`,
              }
            }}
            startIcon={<ShareIcon />}
          >
            COMPARTILHAR MEU ACERVO
          </Button>
        )}

        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          aria-label="Seções do acervo"
          sx={{
            mb: 3,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-scrollButtons': { opacity: 1, color: theme.colors.primary },
            '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.25 },
          }}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
        >
          <Tab value={0} label="Meus Itens" id="acervo-tab-0" aria-controls="acervo-painel-0" />
          <Tab value={2} label="Compartilhados" id="acervo-tab-2" aria-controls="acervo-painel-2" />
          <Tab
            value={3}
            label={`Emprestados${loanedCount > 0 ? ` (${loanedCount})` : ''}`}
            id="acervo-tab-3"
            aria-controls="acervo-painel-3"
          />
          <Tab
            value={1}
            id="acervo-tab-1"
            aria-controls="acervo-painel-1"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Relatórios
                {!isPro && <LockIcon sx={{ fontSize: 16 }} aria-label="disponível no plano Pro" />}
              </Box>
            }
            disabled={!isPro}
          />
        </Tabs>

        {/* Tab 0: Itens */}
        {currentTab === 0 && (
          <Box role="tabpanel" id="acervo-painel-0" aria-labelledby="acervo-tab-0">
            {currentFolder && (
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1.5,
                  borderRadius: theme.radii.medium,
                  backgroundColor: `${theme.colors.primary}0D`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                  <IconButton
                    size="small"
                    aria-label="Voltar para o acervo"
                    onClick={() => setCurrentFolderId(null)}
                    sx={{ color: theme.colors.primary, flexShrink: 0 }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    onClick={() => setCurrentFolderId(null)}
                    sx={{ color: theme.colors.text.secondary, cursor: 'pointer', flexShrink: 0 }}
                  >
                    Acervo
                  </Typography>
                  <ChevronRightIcon sx={{ color: theme.colors.text.secondary, fontSize: '1rem', flexShrink: 0 }} />
                  <FolderOpenIcon sx={{ color: theme.colors.primary, fontSize: '1.1rem', flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      color: theme.colors.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {currentFolder.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    aria-label="Renomear pasta"
                    onClick={() => openRenameFolder(currentFolder)}
                    sx={{ color: theme.colors.primary, '&:hover': { backgroundColor: `${theme.colors.primary}15` } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Excluir pasta"
                    onClick={() => handleDeleteFolderClick(currentFolder)}
                    sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Buscar itens..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.card,
                }
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                    </InputAdornment>
                  ),
                }
              }}
            />

            {/* Category Filter */}
            {categories.length > 1 && (
              <Box sx={{ mb: 3 }}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: theme.radii.medium,
                      backgroundColor: theme.colors.card,
                    }
                  }}
                >
                  <InputLabel shrink>Filtrar por categoria</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Filtrar por categoria"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon sx={{ color: theme.colors.text.secondary, ml: 1 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all">
                      <em>Todas as categorias</em>
                    </MenuItem>
                    {categories.filter(cat => cat !== 'all').sort().map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedCategory !== 'all' && (
                    <Chip
                      label={selectedCategory}
                      onDelete={() => setSelectedCategory('all')}
                      color="primary"
                      size="small"
                      sx={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.text.contrast,
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {currentFolderId === null && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                    Pastas
                  </Typography>
                  <Button
                    size="small"
                    onClick={openCreateFolder}
                    startIcon={<CreateNewFolderIcon />}
                    sx={{
                      color: theme.colors.primary,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: `${theme.colors.primary}10` },
                    }}
                  >
                    Nova pasta
                  </Button>
                </Box>

                {folders.length === 0 ? (
                  <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                    Crie pastas para organizar seu acervo, como "Coisas de cozinha" ou "Roupas de cama".
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {folders.map((folder) => (
                      <Grid key={folder.id} size={{ xs: 12, sm: 6 }}>
                        <Card
                          onClick={() => setCurrentFolderId(folder.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            cursor: 'pointer',
                            backgroundColor: `${theme.colors.primary}0D`,
                            borderRadius: theme.radii.medium,
                            border: `1px solid ${theme.colors.border}`,
                            boxShadow: 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.shadows.medium,
                              borderColor: theme.colors.primary,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              flexShrink: 0,
                              borderRadius: theme.radii.small,
                              backgroundColor: `${theme.colors.primary}1A`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FolderIcon sx={{ color: theme.colors.primary }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                color: theme.colors.text.primary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {folder.name}
                            </Typography>
                            <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.8rem' }}>
                              {countItemsInFolder(folder.id)} {countItemsInFolder(folder.id) === 1 ? 'item' : 'itens'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton
                              size="small"
                              aria-label="Renomear pasta"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRenameFolder(folder);
                              }}
                              sx={{ color: theme.colors.primary, '&:hover': { backgroundColor: `${theme.colors.primary}15` } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Excluir pasta"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFolderClick(folder);
                              }}
                              sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Stats + View Toggle */}
            {items.length > 0 && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: theme.colors.text.secondary }}>
                  {displayItems.length} {displayItems.length === 1 ? 'item' : 'itens'}
                  {currentFolder ? ' nesta pasta' : ' no acervo'}
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, value) => value && setViewMode(value)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: `1px solid ${theme.colors.border}`,
                      color: theme.colors.text.secondary,
                      padding: '4px 8px',
                      '&.Mui-selected': {
                        backgroundColor: `${theme.colors.primary}20`,
                        color: theme.colors.primary,
                        '&:hover': {
                          backgroundColor: `${theme.colors.primary}30`,
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="list" aria-label="list view">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="grid" aria-label="grid view">
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Empty State */}
            {items.length === 0 && folders.length === 0 ? (
              <EmptyInventoryState
                title="Seu acervo está vazio"
                description="Adicione itens ao seu acervo para começar a organizar seus eventos."
                showDialog={true}
              />
            ) : displayItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: theme.colors.text.secondary, mb: 2 }}>
                  {searchTerm.trim()
                    ? `Nenhum item encontrado para "${searchTerm}"`
                    : currentFolder
                      ? 'Esta pasta está vazia. Mova itens para cá pelo ícone de pasta no item ou pelo formulário.'
                      : 'Nenhum item solto no acervo. Seus itens estão organizados nas pastas acima.'}
                </Typography>
              </Box>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <Grid container spacing={2}>
                {displayItems.map((item) => (
                  <Grid key={item.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Card
                      onClick={() => navigate(`/inventory/${item.id}`)}
                      sx={{
                        borderRadius: theme.radii.medium,
                        boxShadow: 'none',
                        backgroundColor: theme.colors.card,
                        border: `1px solid ${theme.colors.border}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows.medium,
                          borderColor: theme.colors.primary,
                        }
                      }}
                    >
                      {/* Action buttons */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          gap: 0.5,
                          backgroundColor: `${theme.colors.background}E0`,
                          borderRadius: theme.radii.small,
                          padding: '2px',
                          zIndex: 2,
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label="Mover para pasta"
                          onClick={(e) => openMoveMenu(e, item.id)}
                          sx={{
                            color: theme.colors.text.secondary,
                            padding: '4px',
                            '&:hover': { backgroundColor: `${theme.colors.primary}15` },
                          }}
                        >
                          <MoveToFolderIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditClick(e, item.id)}
                          sx={{
                            color: theme.colors.primary,
                            padding: '4px',
                            '&:hover': { backgroundColor: `${theme.colors.primary}15` },
                          }}
                        >
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, item.id)}
                          sx={{
                            color: '#d32f2f',
                            padding: '4px',
                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>

                      {/* Item photo */}
                      {item.photoUrl ? (
                        <CardMedia
                          component="img"
                          image={item.photoUrl}
                          alt={item.itemName}
                          sx={{
                            aspectRatio: '1 / 1',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            aspectRatio: '1 / 1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                          }}
                        >
                          <Inventory2Icon sx={{ fontSize: '3rem', opacity: 0.4, color: theme.colors.text.secondary }} />
                        </Box>
                      )}

                      {/* Item info */}
                      <Box sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            color: theme.colors.text.primary,
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }}
                        >
                          {item.itemName}
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.colors.text.secondary,
                            fontSize: '0.7rem',
                            textAlign: 'center',
                          }}
                        >
                          Qtd: {item.quantity || 1}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              /* List View */
              <Grid container spacing={2}>
                {displayItems.map((item) => (
                  <Grid key={item.id} size={{ xs: 12, md: 6 }}>
                    <Card
                      sx={{
                        display: 'flex',
                        height: 80,
                        backgroundColor: theme.colors.card,
                        borderRadius: theme.radii.medium,
                        border: `1px solid ${theme.colors.border}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows.medium,
                          borderColor: theme.colors.primary,
                        }
                      }}
                    >
                      <Box
                        onClick={() => navigate(`/inventory/${item.id}`)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flex: 1,
                          px: 2,
                          minWidth: 0,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            flexShrink: 0,
                            borderRadius: theme.radii.small,
                            backgroundImage: item.photoUrl
                              ? `url(${item.photoUrl})`
                              : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {!item.photoUrl && (
                            <InventoryIcon sx={{ fontSize: '2.5rem', opacity: 0.5, color: theme.colors.text.secondary }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              color: theme.colors.text.primary,
                              fontSize: '1rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.itemName}
                          </Typography>
                          <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                            Qtd: {item.quantity || 1}
                          </Typography>
                          {isLoaned(item) && (
                            <Typography sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: isOverdue(item.loan) ? '#d32f2f' : theme.colors.primary,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {describeLoan(item.loan)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center', pr: 2 }}>
                        <IconButton
                          size="small"
                          aria-label="Mover para pasta"
                          onClick={(e) => openMoveMenu(e, item.id)}
                          sx={{
                            color: theme.colors.text.secondary,
                            width: 36,
                            height: 36,
                            '&:hover': {
                              backgroundColor: `${theme.colors.primary}15`,
                            }
                          }}
                        >
                          <MoveToFolderIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditClick(e, item.id)}
                          sx={{
                            color: theme.colors.primary,
                            width: 36,
                            height: 36,
                            '&:hover': {
                              backgroundColor: `${theme.colors.primary}15`,
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, item.id)}
                          sx={{
                            color: '#d32f2f',
                            width: 36,
                            height: 36,
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Tab 1: Relatórios (Pro only) */}
        {currentTab === 1 && isPro && (
          <Box role="tabpanel" id="acervo-painel-1" aria-labelledby="acervo-tab-1">
            {/* Pie Chart */}
            <Card sx={{ p: 2, mb: 3, bgcolor: theme.colors.card }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Itens por Categoria</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={categories
                      .filter(cat => cat !== 'all')
                      .map(cat => ({
                        name: cat || 'Sem categoria',
                        value: items.filter(item => item.category === cat).reduce((sum, item) => sum + (item.quantity || 1), 0)
                      }))}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categories.filter(cat => cat !== 'all').map((_, index) => (
                      <Cell key={`cell-${index}`} fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'][index % 6]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, name: string) => [`${value} un.`, name]} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                    formatter={(value: string) => <span style={{ fontSize: '11px' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Items Table */}
            <Card sx={{ bgcolor: theme.colors.card }}>
              <Typography variant="h6" sx={{ p: 3, pb: 2, fontWeight: 'bold' }}>Resumo de Itens</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Categoria</TableCell>
                      <TableCell align="right">Cadastros</TableCell>
                      <TableCell align="right">Qtd Total</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories
                      .filter(cat => cat !== 'all')
                      .map(cat => {
                        const categoryItems = items.filter(item => item.category === cat);
                        const totalQuantity = categoryItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
                        const totalValue = categoryItems.reduce((sum, item) =>
                          sum + ((item.unitValue || 0) * (item.quantity || 1)), 0
                        );
                        return (
                          <TableRow key={cat}>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{cat || 'Sem categoria'}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{categoryItems.length}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{totalQuantity}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>R$ {totalValue.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    <TableRow sx={{ bgcolor: theme.colors.background }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{items.length}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        {items.reduce((sum, item) => sum + (item.quantity || 1), 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        R$ {items.reduce((sum, item) => sum + ((item.unitValue || 0) * (item.quantity || 1)), 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Box>
        )}

        {/* Tab 2: Inventários Compartilhados */}
        {currentTab === 2 && (
          <Box role="tabpanel" id="acervo-painel-2" aria-labelledby="acervo-tab-2">
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 3 }}>
              Acervos de outros usuários que você tem acesso para visualizar.
            </Typography>

            {loadingShared ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: theme.colors.primary }} />
              </Box>
            ) : sharedInventories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ShareIcon sx={{ fontSize: 48, color: theme.colors.text.secondary, mb: 2 }} />
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1 }}>
                  Nenhum acervo compartilhado com você
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  Quando alguém compartilhar o acervo com você, ele aparecerá aqui.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {sharedInventories.map((access) => (
                  <Card
                    key={access.id}
                    onClick={() => navigate(`/inventory/shared/${access.ownerId}`)}
                    sx={{
                      mb: 2,
                      backgroundColor: theme.colors.card,
                      borderRadius: theme.radii.medium,
                      border: `1px solid ${theme.colors.border}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows.medium,
                        borderColor: theme.colors.primary,
                      }
                    }}
                  >
                    <ListItem sx={{ py: 2 }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            backgroundColor: theme.colors.secondary,
                            color: theme.colors.primary,
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`Acervo de ${access.ownerName}`}
                        secondary={`Compartilhado em ${access.grantedAt.toLocaleDateString('pt-BR')}`}
                        primaryTypographyProps={{
                          sx: { color: theme.colors.text.primary, fontWeight: 600 }
                        }}
                        secondaryTypographyProps={{
                          sx: { color: theme.colors.text.secondary }
                        }}
                      />
                      <ChevronRightIcon sx={{ color: theme.colors.text.secondary }} />
                    </ListItem>
                  </Card>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Tab 3: Emprestados */}
        {currentTab === 3 && (
          <Box role="tabpanel" id="acervo-painel-3" aria-labelledby="acervo-tab-3">
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2, textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem' }}>
              Veja seus itens emprestados:
            </Typography>
            {loanedCount === 0 ? (
              <Typography variant="body2" sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 6 }}>
                Nenhum item emprestado no momento.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {items.filter(item => isLoaned(item)).map((item) => (
                  <Grid key={item.id} size={{ xs: 12, md: 6 }}>
                    <Card
                      onClick={() => navigate(`/inventory/${item.id}`)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        cursor: 'pointer',
                        backgroundColor: theme.colors.card,
                        borderRadius: theme.radii.medium,
                        border: `1px solid ${isOverdue(item.loan) ? '#d32f2f' : theme.colors.border}`,
                        boxShadow: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          flexShrink: 0,
                          borderRadius: theme.radii.small,
                          backgroundImage: item.photoUrl ? `url(${item.photoUrl})` : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 'bold', color: theme.colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.itemName}
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.8rem' }}>
                          Qtd: {item.loan?.quantity ?? 1}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isOverdue(item.loan) ? '#d32f2f' : theme.colors.primary }}>
                          {describeLoan(item.loan)}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}


        {/* FAB Removed */}
      </Box>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Excluir item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Item Type Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="span" sx={{ color: theme.colors.text.primary }}>
            Adicionar Item
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3, color: theme.colors.text.secondary }}>
            Escolha como deseja cadastrar o item:
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={async () => {
                setShowCreateDialog(false);
                navigate('/inventory/new');
              }}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                py: 2,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.9,
                }
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Cadastro Completo
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Com foto, descrição e todos os detalhes
                </Typography>
              </Box>
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setShowCreateDialog(false);
                navigate('/inventory/new?quick=true');
              }}
              sx={{
                borderColor: theme.colors.primary,
                color: theme.colors.primary,
                py: 2,
                '&:hover': {
                  borderColor: theme.colors.primary,
                  backgroundColor: `${theme.colors.primary}10`,
                }
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Cadastro Rápido
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Apenas nome e quantidade
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCreateDialog(false)}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de limite atingido */}
      <LimitReachedDialog
        open={limitDialog.open}
        feature="itens de acervo"
        onClose={() => setLimitDialog({ open: false, message: '' })}
        onUpgrade={() => navigate('/pricing')}
        currentPlan={plan}
      />

      {/* Dialog de compartilhar acesso ao inventário */}
      <ShareInventoryAccessDialog
        open={shareAccessDialogOpen}
        onClose={() => setShareAccessDialogOpen(false)}
      />

      <Dialog
        open={folderDialogOpen}
        onClose={() => !savingFolder && setFolderDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{folderDialogMode === 'create' ? 'Nova pasta' : 'Renomear pasta'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome da pasta"
            placeholder="Ex: Coisas de cozinha"
            value={folderNameInput}
            onChange={(e) => setFolderNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && folderNameInput.trim() && !savingFolder) {
                handleSaveFolder();
              }
            }}
            disabled={savingFolder}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': { borderRadius: theme.radii.medium },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFolderDialogOpen(false)}
            disabled={savingFolder}
            sx={{ color: theme.colors.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveFolder}
            disabled={savingFolder || !folderNameInput.trim()}
            sx={{ color: theme.colors.primary, fontWeight: 'bold' }}
          >
            {savingFolder ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteFolderDialogOpen}
        onClose={() => !deletingFolder && setDeleteFolderDialogOpen(false)}
      >
        <DialogTitle>Excluir pasta?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {folderToDelete
              ? `A pasta "${folderToDelete.name}" será excluída. Os itens dentro dela não serão apagados: voltam para a raiz do acervo.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialogOpen(false)} disabled={deletingFolder}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteFolderConfirm} color="error" disabled={deletingFolder}>
            {deletingFolder ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={closeMoveMenu}
      >
        <MenuItem onClick={() => handleMoveToFolder(null)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
            <Box sx={{ width: 20, display: 'flex', justifyContent: 'center' }}>
              {itemToMove && !getMovingItemFolderId() && <CheckIcon fontSize="small" sx={{ color: theme.colors.primary }} />}
            </Box>
            <Typography sx={{ color: theme.colors.text.primary }}>Raiz (nenhuma)</Typography>
          </Box>
        </MenuItem>
        {folders.map((folder) => (
          <MenuItem key={folder.id} onClick={() => handleMoveToFolder(folder.id)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
              <Box sx={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                {itemToMove && getMovingItemFolderId() === folder.id && (
                  <CheckIcon fontSize="small" sx={{ color: theme.colors.primary }} />
                )}
              </Box>
              <FolderIcon fontSize="small" sx={{ color: theme.colors.primary }} />
              <Typography sx={{ color: theme.colors.text.primary }}>{folder.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <BottomNavigation />
    </>
  );
};
